import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Pencil, Trash2 } from "lucide-react";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import Button from "../Button";
import ConfirmModal from "../ConfirmModal";
import notify from "../../lib/notify";
import useGuardedMutation from "../../hooks/useGuardedMutation";
import EwtRecordEditModal from "./EwtRecordEditModal";
import { GlPreviewModal, GlStatusCell } from "./glUi";
import { formatCentavos, formatRate } from "../../utils/whtMoney";

// Saved EWT records for the client (F2 records API), below the grid — the
// VATRecordsList counterpart. Edit (PUT) and delete both go through
// useGuardedMutation.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const PERIODS = [
  { value: "all", label: "Whole year" },
  ...[1, 2, 3, 4].map((q) => ({ value: `q${q}`, label: `Q${q}` })),
  ...MONTHS.map((m, i) => ({ value: `m${i + 1}`, label: m })),
];

const periodParams = (period) => {
  if (period.startsWith("q")) return { quarter: Number(period.slice(1)) };
  if (period.startsWith("m")) return { month: Number(period.slice(1)) };
  return {};
};

/**
 * Warnings for a stored record: the server's from this session (passed in by
 * record id) plus the two the record itself still shows after a reload.
 */
export function recordWarnings(record, sessionWarnings = {}) {
  const list = [...(sessionWarnings[record._id] || [])];
  const has = (code) => list.some((w) => w.code === code);
  if (record.atcRateBasisPoints != null && record.rateBasisPoints !== record.atcRateBasisPoints && !has("RATE_MISMATCH")) {
    list.push({ code: "RATE_MISMATCH", message: `Rate ${formatRate(record.rateBasisPoints)} differs from the ${record.atcCode} rate of ${formatRate(record.atcRateBasisPoints)}` });
  }
  if (!record.counterpartyTin && !has("PAYEE_NO_TIN")) {
    list.push({ code: "PAYEE_NO_TIN", message: "Payee has no TIN — required on the 2307 and the QAP" });
  }
  return list;
}

const EwtRecordsList = ({ client, year, refreshKey = 0, sessionWarnings = {}, onWarningsChange, onOpenPostingAccounts }) => {
  const [period, setPeriod] = useState("all");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [glModal, setGlModal] = useState(null); // { ids, month? }
  const clientId = client?._id;

  const load = useCallback(async () => {
    if (!clientId || !year) return;
    setLoading(true);
    setError(null);
    try {
      const res = await withholdingTaxAPI.listRecords(clientId, { year, ...periodParams(period) });
      setRecords(res?.data || []);
      setSelected(new Set());
    } catch (e) {
      setError(e.message || "Couldn't load records");
    } finally {
      setLoading(false);
    }
  }, [clientId, year, period]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const doDelete = async () => {
    const record = pendingDelete;
    try {
      await withholdingTaxAPI.deleteRecord(clientId, record._id);
      setPendingDelete(null);
      notify.success("Deleted", "The withholding record was deleted.");
      await load();
    } catch (e) {
      notify.error("Couldn't delete", e.message || "Try again.");
    }
  };
  const { mutate: runDelete, isPending: deleting } = useGuardedMutation(doDelete);

  const unposted = records.filter((r) => !r.isPostedToGL && r.glStatus !== "posting");
  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = unposted.length > 0 && unposted.every((r) => selected.has(r._id));
  const periodMonth = period.startsWith("m") ? Number(period.slice(1)) : null;

  const totals = records.reduce(
    (acc, r) => ({ base: acc.base + (r.baseCentavos || 0), tax: acc.tax + (r.taxWithheldCentavos || 0) }),
    { base: 0, tax: 0 }
  );

  return (
    <section aria-labelledby="ewt-records-heading" className="mt-8">
      <div className="flex items-end justify-between gap-4 mb-3">
        <div>
          <h3 id="ewt-records-heading" className="text-lg font-medium text-ink">Saved EWT records</h3>
          <p className="text-sm text-ink-body">{year}</p>
        </div>
        <label className="text-sm text-ink-body">
          <span className="mr-2">Period</span>
          <select
            aria-label="Period"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="border border-ink-line rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
      </div>

      {error && <p role="alert" className="text-sm text-danger-500 mb-2">{error}</p>}

      <div className="flex flex-wrap items-center justify-end gap-2 mb-2">
        <Button variant="outline" size="sm" disabled={!selected.size} onClick={() => setGlModal({ ids: [...selected] })}>
          Preview & post ({selected.size})
        </Button>
        <Button variant="outline" size="sm" disabled={!periodMonth || !unposted.length}
          title={periodMonth ? undefined : "Pick a month in Period first"}
          onClick={() => setGlModal({ ids: unposted.map((r) => r._id), month: { year, month: periodMonth, kind: "EWT" } })}>
          Post all unposted (month)
        </Button>
      </div>

      <div className="overflow-x-auto border border-ink-line rounded-lg">
        <table className="min-w-full divide-y divide-ink-line">
          <thead className="bg-ink-canvas">
            <tr>
              <th className="px-3 py-3 w-8">
                <input type="checkbox" aria-label="Select all unposted" checked={allSelected} disabled={!unposted.length}
                  onChange={(e) => setSelected(e.target.checked ? new Set(unposted.map((r) => r._id)) : new Set())} className="h-4 w-4 accent-primary-500" />
              </th>
              {["Month", "Description", "Payee", "ATC", "Tax base", "Rate %", "Tax withheld", "", "GL", "Actions"].map((h, i) => (
                <th key={h || `col-${i}`} className={`px-3 py-3 text-xs font-medium text-ink-body uppercase tracking-wider ${i >= 4 && i <= 6 ? "text-right" : "text-left"}`}>
                  {h || <span className="sr-only">Warnings</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-ink-line">
            {loading && records.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-sm text-ink-body">Loading…</td></tr>
            )}
            {!loading && records.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-sm text-ink-body">No records for this period.</td></tr>
            )}
            {records.map((r) => {
              const warnings = recordWarnings(r, sessionWarnings);
              return (
                <tr key={r._id} data-record-id={r._id}>
                  <td className="px-3 py-2">
                    <input type="checkbox" aria-label={`Select ${r.counterpartyName} ${r.atcCode}`} checked={selected.has(r._id)}
                      disabled={r.isPostedToGL || r.glStatus === "posting"} onChange={() => toggle(r._id)} className="h-4 w-4 accent-primary-500" />
                  </td>
                  <td className="px-3 py-2 text-sm text-ink">{MONTHS[r.month - 1]}</td>
                  <td className="px-3 py-2 text-sm text-ink">{r.description || "—"}</td>
                  <td className="px-3 py-2 text-sm text-ink">
                    {r.counterpartyName}
                    {r.counterpartyTin && <span className="block text-xs text-ink-body tabular-nums">{r.counterpartyTin}-{r.counterpartyBranch}</span>}
                  </td>
                  <td className="px-3 py-2 text-sm text-ink">{r.atcCode}</td>
                  <td className="px-3 py-2 text-sm text-ink text-right tabular-nums">₱{formatCentavos(r.baseCentavos)}</td>
                  <td className="px-3 py-2 text-sm text-ink text-right tabular-nums">{formatRate(r.rateBasisPoints)}</td>
                  <td className="px-3 py-2 text-sm text-ink text-right tabular-nums">₱{formatCentavos(r.taxWithheldCentavos)}</td>
                  <td className="px-3 py-2">
                    {warnings.length > 0 && (
                      <span title={warnings.map((w) => w.message).join("\n")} aria-label={`Warnings: ${warnings.map((w) => w.message).join("; ")}`}>
                        <AlertTriangle className="w-4 h-4 text-warning-500" />
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2"><GlStatusCell record={r} /></td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" icon={Pencil} onClick={() => setEditing(r)} aria-label={`Edit ${r.counterpartyName} ${r.atcCode}`} />
                      <Button variant="dangerGhost" size="sm" icon={Trash2} onClick={() => setPendingDelete(r)} aria-label={`Delete ${r.counterpartyName} ${r.atcCode}`} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {records.length > 0 && (
            <tfoot className="bg-ink-canvas border-t-2 border-ink-line">
              <tr>
                <td colSpan={5} className="px-3 py-3 text-sm font-medium text-ink-body">{records.length} record{records.length !== 1 ? "s" : ""}</td>
                <td className="px-3 py-3 text-sm font-medium text-ink text-right tabular-nums">₱{formatCentavos(totals.base)}</td>
                <td />
                <td className="px-3 py-3 text-sm font-medium text-ink text-right tabular-nums">₱{formatCentavos(totals.tax)}</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {editing && (
        <EwtRecordEditModal
          client={client}
          record={editing}
          onOpenPostingAccounts={onOpenPostingAccounts && (() => { setEditing(null); onOpenPostingAccounts(); })}
          onClose={() => setEditing(null)}
          onSaved={async (updated, warnings) => {
            setEditing(null);
            onWarningsChange?.(updated._id, warnings);
            await load();
          }}
        />
      )}

      {glModal && (
        <GlPreviewModal
          client={client}
          ids={glModal.ids}
          month={glModal.month}
          onClose={() => setGlModal(null)}
          onPosted={load}
          onOpenPostingAccounts={onOpenPostingAccounts && (() => { setGlModal(null); onOpenPostingAccounts(); })}
        />
      )}

      <ConfirmModal
        isOpen={Boolean(pendingDelete)}
        title="Delete this withholding record?"
        message={pendingDelete
          ? `${pendingDelete.counterpartyName} · ${pendingDelete.atcCode} · ₱${formatCentavos(pendingDelete.baseCentavos)}. ${pendingDelete.isPostedToGL ? "This record is posted — this will post a reversal. " : ""}This can't be undone.`
          : ""}
        confirmLabel="Delete"
        loading={deleting}
        onConfirm={() => runDelete()}
        onCancel={() => setPendingDelete(null)}
      />
    </section>
  );
};

export default EwtRecordsList;
